import type { OrderStatus } from '@momonos/shared'
import { Flame, Leaf, Star } from 'lucide-react'
import type { HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

export type BadgeTone = 'neutral' | 'red' | 'yellow' | 'green' | 'blue'

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-overlay text-content-secondary border-surface-border',
  red: 'bg-momo-red/15 text-momo-red border-momo-red/35',
  yellow: 'bg-momo-yellow/15 text-momo-yellow border-momo-yellow/35',
  green: 'bg-success/15 text-success border-success/35',
  blue: 'bg-info/15 text-info border-info/35',
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone
  size?: 'sm' | 'md'
}

export function Badge({
  tone = 'neutral',
  size = 'sm',
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2.5 py-0.5 text-xs' : 'px-3 py-1 text-sm',
        TONES[tone],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

/** FR-CAT-2 — bestseller marker. */
export function BestsellerBadge({ label = 'Bestseller' }: { label?: string }) {
  return (
    <Badge tone="yellow">
      <Star aria-hidden="true" className="size-3 fill-current" />
      {label}
    </Badge>
  )
}

/** FR-CAT-2 — veg marker; the icon alone never carries the meaning (NFR-8). */
export function VegBadge({ label = 'Veg' }: { label?: string }) {
  return (
    <Badge tone="green">
      <Leaf aria-hidden="true" className="size-3" />
      {label}
    </Badge>
  )
}

/**
 * FR-CAT-2 — spice level 0-3. Rendered as filled flames plus an accessible
 * label, so it works for colour-blind customers and screen readers alike.
 */
export function SpiceLevel({
  level,
  labels = ['No spice', 'Mild', 'Medium', 'Hot'],
  className,
}: {
  level: 0 | 1 | 2 | 3
  labels?: readonly string[]
  className?: string
}) {
  const SPICE_CLASS = ['text-spice-0', 'text-spice-1', 'text-spice-2', 'text-spice-3'] as const
  // Named rather than index-keyed: the three flames are fixed positions, not a list.
  const FLAME_SLOTS = ['mild', 'medium', 'hot'] as const

  return (
    // role="img" so the flames read as one labelled graphic rather than as
    // three meaningless icons — the label is the only thing a screen reader
    // announces here.
    <span
      role="img"
      className={cn('inline-flex items-center gap-0.5', className)}
      aria-label={labels[level] ?? `Spice level ${level}`}
      title={labels[level]}
    >
      {level === 0 ? (
        <span className="text-content-muted text-xs">{labels[0]}</span>
      ) : (
        FLAME_SLOTS.map((slot, index) => (
          <Flame
            key={slot}
            aria-hidden="true"
            className={cn(
              'size-4',
              index < level ? cn(SPICE_CLASS[level], 'fill-current') : 'text-surface-border',
            )}
          />
        ))
      )}
    </span>
  )
}

/** FR-KDS-2 / FR-POS-1 — order status pill, coloured per KDS column. */
const STATUS_TONE: Record<OrderStatus, BadgeTone> = {
  pending_payment: 'neutral',
  placed: 'blue',
  accepted: 'blue',
  preparing: 'yellow',
  ready: 'green',
  delivered: 'neutral',
  cancelled: 'red',
  refunded: 'red',
}

export function StatusBadge({
  status,
  label,
  className,
}: {
  status: OrderStatus
  label?: string
  className?: string
}) {
  return (
    <Badge tone={STATUS_TONE[status]} className={cn('uppercase tracking-wide', className)}>
      {label ?? status.replace(/_/g, ' ')}
    </Badge>
  )
}
